import React, { useState, useRef, useEffect } from 'react';

export interface DataTableEntry {
  id: string;
  name: string;
}

export interface AnalysisEntry {
  id: string;
  label: string;
  type: string;
  timestamp: number;
}

export interface GraphEntry {
  id: string;
  label: string;
  type: string;
}

interface SpectraNavigatorProps {
  tables: DataTableEntry[];
  activeTableId: string | null;
  analyses: AnalysisEntry[];
  graphs: GraphEntry[];
  onSelectTable: (id: string) => void;
  onAddTable: () => void;
  onRenameTable: (id: string, name: string) => void;
  onSelectAnalysis: (id: string) => void;
  onSelectGraph: (id: string) => void;
}

const SectionHeader: React.FC<{ title: string; count: number; onAdd?: () => void }> = ({ title, count, onAdd }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px 4px',
  }}>
    <span style={{
      fontSize: '10px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: 'var(--text-secondary)',
    }}>
      {title} ({count})
    </span>
    {onAdd && (
      <button
        onClick={onAdd}
        style={{
          width: '18px',
          height: '18px',
          borderRadius: '4px',
          border: '1px solid var(--card-border)',
          background: 'var(--hover-bg)',
          color: 'var(--text-secondary)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '14px',
          lineHeight: 1,
          padding: 0,
        }}
        title={`Add ${title.toLowerCase()}`}
      >
        +
      </button>
    )}
  </div>
);

const SpectraNavigator: React.FC<SpectraNavigatorProps> = ({
  tables,
  activeTableId,
  analyses,
  graphs,
  onSelectTable,
  onAddTable,
  onRenameTable,
  onSelectAnalysis,
  onSelectGraph,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingId]);

  const startRename = (id: string, currentName: string) => {
    setEditingId(id);
    setEditValue(currentName);
  };

  const commitRename = () => {
    if (editingId && editValue.trim()) {
      onRenameTable(editingId, editValue.trim());
    }
    setEditingId(null);
  };

  const typeBadgeColor = (type: string) => {
    const colors: Record<string, string> = {
      unpaired_t: '#6366f1',
      paired_t: '#8b5cf6',
      welch_t: '#a78bfa',
      mann_whitney: '#7c3aed',
      one_way_anova: '#2563eb',
      kruskal_wallis: '#3b82f6',
      pearson: '#059669',
      spearman: '#10b981',
      linear_regression: '#14b8a6',
      descriptive: '#f59e0b',
    };
    return colors[type] || '#6b7280';
  };

  return (
    <div style={{
      width: '180px',
      minWidth: '180px',
      borderRight: '1px solid var(--card-border)',
      background: 'var(--card-bg)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Data Tables */}
      <SectionHeader title="Data Tables" count={tables.length} onAdd={onAddTable} />
      <div style={{ flex: '0 0 auto', overflowY: 'auto', maxHeight: '200px' }}>
        {tables.map(table => (
          <div
            key={table.id}
            onClick={() => onSelectTable(table.id)}
            onDoubleClick={() => startRename(table.id, table.name)}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              cursor: 'pointer',
              background: activeTableId === table.id ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
              color: activeTableId === table.id ? 'rgb(129, 120, 248)' : 'var(--text-primary)',
              borderLeft: activeTableId === table.id ? '2px solid rgb(99, 102, 241)' : '2px solid transparent',
              transition: 'all 0.1s ease',
            }}
          >
            {editingId === table.id ? (
              <input
                ref={inputRef}
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onBlur={commitRename}
                onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setEditingId(null); }}
                style={{
                  width: '100%',
                  background: 'var(--input-bg)',
                  border: '1px solid var(--card-border)',
                  borderRadius: '3px',
                  color: 'var(--text-primary)',
                  fontSize: '12px',
                  padding: '2px 4px',
                  outline: 'none',
                }}
              />
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '12px', height: '12px', opacity: 0.5 }}>
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="3" y1="9" x2="21" y2="9" />
                  <line x1="9" y1="21" x2="9" y2="9" />
                </svg>
                {table.name}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Divider */}
      <div style={{ height: '1px', background: 'var(--divider)', margin: '4px 0' }} />

      {/* Analyses */}
      <SectionHeader title="Analyses" count={analyses.length} />
      <div style={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
        {analyses.length === 0 && (
          <div style={{ padding: '8px 12px', fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            Run an analysis to see results here
          </div>
        )}
        {analyses.map(a => (
          <div
            key={a.id}
            onClick={() => onSelectAnalysis(a.id)}
            style={{
              padding: '5px 12px',
              fontSize: '11px',
              cursor: 'pointer',
              color: 'var(--text-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span style={{
              display: 'inline-block',
              padding: '1px 5px',
              borderRadius: '3px',
              fontSize: '9px',
              fontWeight: 600,
              background: typeBadgeColor(a.type) + '22',
              color: typeBadgeColor(a.type),
              textTransform: 'uppercase',
            }}>
              {a.type.replace(/_/g, ' ').slice(0, 8)}
            </span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.label}</span>
          </div>
        ))}
      </div>

      {/* Divider */}
      <div style={{ height: '1px', background: 'var(--divider)', margin: '4px 0' }} />

      {/* Graphs */}
      <SectionHeader title="Graphs" count={graphs.length} />
      <div style={{ flex: '0 0 auto', overflowY: 'auto', maxHeight: '150px' }}>
        {graphs.length === 0 && (
          <div style={{ padding: '8px 12px', fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            Graphs appear after analysis
          </div>
        )}
        {graphs.map(g => (
          <div
            key={g.id}
            onClick={() => onSelectGraph(g.id)}
            style={{
              padding: '5px 12px',
              fontSize: '11px',
              cursor: 'pointer',
              color: 'var(--text-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '11px', height: '11px', opacity: 0.5 }}>
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SpectraNavigator;
